import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Clock,
  LogOut,
  Users,
  FolderKanban,
  CheckSquare,
  LayoutDashboard,
  CalendarClock,
  Lock,
} from "lucide-react";

function NavItem({ to, icon: Icon, children, testid }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150 ${
          isActive
            ? "bg-[#111111] text-white"
            : "text-neutral-700 hover:bg-neutral-100"
        }`
      }
    >
      <Icon size={16} strokeWidth={2} />
      <span>{children}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      toast.error("Current password and new password are required.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match.");
      return;
    }
    try {
      await api.post("/auth/change-password", {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      toast.success("Password updated successfully.");
      setPasswordOpen(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F9F9F8]">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-[#E5E5E5] bg-white flex md:flex-col">
        <div className="p-6 border-r md:border-r-0 md:border-b border-[#E5E5E5] flex items-center gap-3">
          <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center">
            <Clock size={18} className="text-white" />
          </div>
          <div className="hidden md:block">
            <div className="font-display font-black text-lg leading-none">TIMEBLOCK</div>
            <div className="text-[10px] text-neutral-500 tracking-widest uppercase mt-1">
              Time Accounting
            </div>
          </div>
        </div>

        <nav className="flex md:flex-col gap-1 p-3 md:p-4 flex-1 overflow-x-auto">
          {isAdmin ? (
            <>
              <NavItem to="/admin" icon={LayoutDashboard} testid="nav-admin-dashboard">
                Dashboard
              </NavItem>
              <NavItem to="/admin/approvals" icon={CheckSquare} testid="nav-admin-approvals">
                Approvals
              </NavItem>
              <NavItem to="/admin/projects" icon={FolderKanban} testid="nav-admin-projects">
                Projects
              </NavItem>
              <NavItem to="/admin/staff" icon={Users} testid="nav-admin-staff">
                Staff
              </NavItem>
              <NavItem to="/admin/reports" icon={CalendarClock} testid="nav-admin-reports">
                Reports
              </NavItem>
            </>
          ) : (
            <>
              <NavItem to="/" icon={CalendarClock} testid="nav-staff-timesheet">
                My Timesheet
              </NavItem>
              <NavItem to="/history" icon={CheckSquare} testid="nav-staff-history">
                History
              </NavItem>
            </>
          )}
        </nav>

        <div className="hidden md:block p-4 border-t border-[#E5E5E5]">
          <div className="text-xs text-neutral-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-name">
            {user?.name}
          </div>
          <div className="text-xs text-neutral-500 truncate" data-testid="current-user-email">
            {user?.email}
          </div>
          <Button
            variant="outline"
            onClick={() => setPasswordOpen(true)}
            className="mt-3 w-full justify-center rounded-sm"
          >
            <Lock size={14} className="mr-2" /> Change password
          </Button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="mt-3 w-full inline-flex items-center gap-2 justify-center border border-[#E5E5E5] px-3 py-2 text-sm rounded-sm hover:bg-neutral-100 transition-colors"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </aside>

      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent className="rounded-md sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Change password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Current password</Label>
              <Input
                type="password"
                className="mt-2 rounded-sm"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">New password</Label>
              <Input
                type="password"
                className="mt-2 rounded-sm"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Confirm new password</Label>
              <Input
                type="password"
                className="mt-2 rounded-sm"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={handlePasswordChange} className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white">
              Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Content */}
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}
